import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useState, useContext, useMemo, useEffect } from 'react';
import { Button, Icon, InlineNotification, Input, Select, Spinner, Table, Tooltip, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { PageWrapper } from 'libs.react';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { getCubeApi } from '../../hooks/use_cube_api/use_cube_api.hook';
const DataReportsBuildPage = ({ studies = [] }) => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const webClient = useApiClient(ApiClientService.WEB);
    const defaultStudyOption = {
        label: translate('Select a study'),
        value: '',
    };
    const defaultEnvOption = {
        label: translate('Select an environment'),
        value: '',
    };
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedStudy, setSelectedStudy] = useState(defaultStudyOption);
    const [selectedEnv, setSelectedEnv] = useState(defaultEnvOption);
    const [isLoading, setIsLoading] = useState(false);
    const [queryResult, setQueryResult] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [columns, setColumns] = useState([]);
    const [selectedDataView, setSelectedDataView] = useState({
        label: translate('Select a dataset'),
        value: '',
    });
    const [selectedDataFields, setSelectedDataFields] = useState(new Set());
    const [selectionOrder, setSelectionOrder] = useState([]);
    const [didQueryRun, setDidQueryRun] = useState(false);
    const studyOptions = studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    }));
    const fetchEnvs = async () => {
        try {
            setSelectedEnv(defaultEnvOption);
            const [devEnvironmentsResponse] = await Promise.all([
                momClient.get(`/v1/entities/${entityId}/studies/${selectedStudy.value}/environments`, {
                    params: {
                        clusterTypes: 'dev',
                        withCortexAccess: true,
                    },
                }),
            ]);
            return [...devEnvironmentsResponse.data.data.filter((env) => !env.deployment.isProd)];
        }
        catch (error) {
            toast.addNotification({
                type: 'error',
                title: error.message ||
                    error.error.message ||
                    translate('An error occurred while fetching environments. Please try again.'),
            });
        }
    };
    const { data: environments = [], isLoading: isFetchingEnvs } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudy.value, 'dev-with-cortex-access'],
        queryFn: fetchEnvs,
        enabled: !!entityId && !!selectedStudy.value,
        refetchOnWindowFocus: false,
    });
    const envOptions = environments.map((env) => ({
        label: env.name,
        value: env.cortexId,
    }));
    const fetchDataTransferViews = async () => {
        const { data } = await webClient.post('/api/data_transfer/views/list', {
            filter: {
                includeCubes: true,
            },
        });
        return data;
    };
    const { data: dataTransferViews = [], isLoading: isFetchingDataTransferViews } = useQuery({
        queryKey: ['entities', entityId, 'data-transfer', 'views'],
        queryFn: fetchDataTransferViews,
        enabled: !!entityId,
        refetchOnWindowFocus: false,
    });
    const dataViewOptions = useMemo(() => {
        const viewsArray = Array.isArray(dataTransferViews) ? dataTransferViews : dataTransferViews?.data || [];
        return viewsArray.map((view) => ({
            label: view.title,
            value: view.name,
        }));
    }, [dataTransferViews]);
    useEffect(() => {
        if (isInitialized) {
            return;
        }
        if (dataViewOptions.length > 0) {
            setSelectedDataView(dataViewOptions[0]);
            setIsInitialized(true);
        }
    }, [dataViewOptions, isInitialized]);
    useEffect(() => {
        const fieldsArray = Array.from(selectedDataFields);
        const orderedColumns = selectionOrder.map((orderItem) => {
            const field = fieldsArray.find((f) => f.id === orderItem.id && f.type === orderItem.type);
            return {
                header: field?.shortTitle || orderItem.id,
                accessorFn: (row) => row[orderItem.id],
            };
        });
        setColumns(orderedColumns);
    }, [selectedDataFields, selectionOrder]);
    const filteredDataFields = useMemo(() => {
        const viewsArray = Array.isArray(dataTransferViews) ? dataTransferViews : dataTransferViews?.data || [];
        const selectedView = viewsArray.find((view) => view.name === selectedDataView.value);
        if (!selectedView) {
            return [];
        }
        const dimensionsWithType = selectedView.dimensions.map((dimension) => ({
            ...dimension,
            fieldType: 'dimension',
        }));
        const measuresWithType = selectedView.measures.map((measure) => ({
            ...measure,
            fieldType: 'measure',
        }));
        const allFields = [...dimensionsWithType, ...measuresWithType];
        return allFields.filter((field) => field.title.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [dataTransferViews, searchTerm, selectedDataView]);
    const selectedFieldsLookup = useMemo(() => new Set(Array.from(selectedDataFields).map((f) => `${f.id}:${f.type}`)), [selectedDataFields]);
    const handleDataViewChange = (option) => {
        setSelectedDataView(option);
        setSelectedDataFields(new Set());
        setSelectionOrder([]);
    };
    const handleDataFieldSelect = (fieldId, fieldType, fieldShortTitle) => {
        const newSelected = new Set(selectedDataFields);
        const newOrder = [...selectionOrder];
        const fieldObj = { id: fieldId, type: fieldType, shortTitle: fieldShortTitle };
        const existingField = Array.from(newSelected).find((field) => field.id === fieldId && field.type === fieldType);
        if (existingField) {
            newSelected.delete(existingField);
            const orderIndex = newOrder.findIndex((item) => item.id === fieldId && item.type === fieldType);
            if (orderIndex !== -1) {
                newOrder.splice(orderIndex, 1);
            }
        }
        else {
            newSelected.add(fieldObj);
            newOrder.push({ id: fieldId, type: fieldType });
        }
        setSelectedDataFields(newSelected);
        setSelectionOrder(newOrder);
    };
    const prepareQuery = (selectedDataFields) => {
        const fieldsArray = Array.from(selectedDataFields);
        const query = {
            dimensions: fieldsArray.filter((field) => field.type === 'dimension').map((field) => field.id),
            measures: fieldsArray.filter((field) => field.type === 'measure').map((field) => field.id),
        };
        return query;
    };
    const handleRunQuery = async () => {
        try {
            if (selectedDataFields.size === 0) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Please select at least one data field before running a query'),
                });
                return;
            }
            setIsLoading(true);
            setQueryResult([]);
            const cubeApi = getCubeApi({
                entityId: entityId.toString(),
                studyId: selectedStudy.value,
                activeReportsTab: 0,
                selectedTestEnv: selectedEnv,
                prodOrgs: [],
            });
            const backendQuery = prepareQuery(selectedDataFields);
            const response = await cubeApi.load(backendQuery, {
                subscribe: true,
            });
            const rawColumns = response?.tableColumns() || [];
            const processedColumns = rawColumns.map((column) => ({
                header: column.shortTitle,
                accessorFn: (row) => row[column.dataIndex],
            }));
            setColumns(processedColumns);
            setQueryResult(response?.tablePivot() ?? []);
        }
        catch (error) {
            const axiosError = error;
            const errorMessage = axiosError.response?.data?.message ||
                translate('An error occurred while running the query. Please try again.');
            toast.addNotification({
                type: 'error',
                title: errorMessage,
            });
        }
        finally {
            setIsLoading(false);
            setDidQueryRun(true);
        }
    };
    return (_jsx(PageWrapper, { children: _jsxs("div", { className: 'grid grid-cols-2 lg:grid-cols-[1.5fr_2fr] gap-4 size-full', children: [_jsxs("div", { className: 'flex flex-col space-y-4 min-w-0', children: [_jsxs("div", { className: 'flex justify-between items-center', children: [_jsx("div", { children: translate('Data Reports Builder') }), _jsx(Button, { disabled: !selectedEnv.value || isLoading, label: translate('Run'), onClick: handleRunQuery })] }), _jsxs("div", { className: 'grid grid-cols-2 gap-4 border-b border-gray-100 pb-4 mb-4', children: [_jsx(Select, { placeholder: defaultStudyOption.label, options: studyOptions, value: selectedStudy, onChange: (option) => setSelectedStudy(option), width: 'full' }), _jsx(Select, { disabled: isFetchingEnvs || !selectedStudy.value, placeholder: defaultEnvOption.label, options: envOptions, value: selectedEnv, onChange: (option) => setSelectedEnv(option), width: 'full' })] }), _jsxs("div", { className: 'grid grid-cols-2 gap-4', children: [_jsx(Input, { id: 'search-data-fields', placeholder: translate('Search...'), value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), width: 'full' }), _jsx(Select, { placeholder: selectedDataView.label, options: dataViewOptions, value: selectedDataView, onChange: handleDataViewChange, width: 'full', disabled: isFetchingDataTransferViews })] }), _jsx("div", { children: _jsx(InlineNotification, { subtitle: translate('Changing the dataset will reset selected data fields'), title: '', type: 'info' }) }), _jsx("div", { className: 'flex-1 overflow-y-auto max-h-[500px]', children: isFetchingDataTransferViews ? (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) })) : filteredDataFields && filteredDataFields.length > 0 ? (filteredDataFields.map((field) => {
                                const isSelected = selectedFieldsLookup.has(`${field.name}:${field.fieldType}`);
                                return (_jsxs("div", { className: `grid grid-cols-[1fr_auto_auto] items-center gap-3 py-3 px-4 cursor-pointer border-b border-border-primary last:border-b-0 ${isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`, onClick: () => handleDataFieldSelect(field.name, field.fieldType, field.shortTitle), children: [_jsxs("div", { className: 'min-w-0', children: [_jsx("div", { children: field.shortTitle }), _jsx("div", { className: 'text-xs text-text-secondary', children: field.fieldType.charAt(0).toUpperCase() + field.fieldType.slice(1) })] }), _jsx("div", { className: 'text-text-secondary', children: field.type.charAt(0).toUpperCase() + field.type.slice(1) }), _jsx("div", { children: _jsx("div", { className: 'flex items-center justify-center', children: _jsx(Tooltip, { title: field.description, children: _jsx(Icon, { name: 'info' }) }) }) })] }, field.name));
                            })) : (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx("div", { className: 'text-center text-gray-500', children: translate('No dimensions or measures found, try selecting a different dataset.') }) })) })] }), _jsxs("div", { className: 'border-l px-4 space-y-4 min-w-0 overflow-hidden', children: [_jsx("div", { className: 'flex justify-between items-center', children: _jsx("div", { children: translate('Data preview') }) }), _jsx("div", { className: 'pb-4 overflow-auto', children: _jsx(Table, { columns: columns, data: queryResult, isLoading: isLoading, labelNoResults: didQueryRun
                                    ? translate('No data available for the selected dimensions or measures.')
                                    : translate('No data available, try running a query first.') }) })] })] }) }));
};
export default DataReportsBuildPage;
